// @flow
import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Grid,
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
} from '@material-ui/core';
import {InfoOutlined} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import {getBasicDomain} from '../../../../utils/component';
import {useCategories} from '../../../categories/utils';
import {useStoreInfo} from '../../../store/utils';
import type {
  Category,
  Subcategory,
  SubcategoryItem,
  ProductDetail,
} from '../../../../entities';

type Props = {
  product?: ProductDetail,
  onChange: (
    categories: Category[],
    subCategories: Subcategory[],
    subCategoryItems: SubcategoryItem[]
  ) => void,
  disabled?: boolean,
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
    background: 'white',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  helperText: {
    color: colors.grey600,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  categoryTooltip: {
    backgroundColor: colors.grey500,
  },
  infoBtn: {
    color: '#9595AB',
    marginLeft: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0, 2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  subcategorySection: {
    marginTop: '.5rem',
  },
  subcategoryTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.grey900,
    textTransform: 'capitalize',
    marginBottom: '.5rem',
  },
  selectWrapper: {},
  select: {
    borderRadius: '8px',
  },
  categoryChip: {
    marginTop: '8px',
  },
}));

export default function ProductFormCategory(props: Props) {
  const {product, onChange, disabled = false} = props;
  const classes = useStyles();

  const categories = useCategories();
  const store = useStoreInfo();

  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [subcategoriesEnabled, setSubcategoriesEnabled] = useState<boolean>(
    false
  );
  const [
    subCategoryItemsEnabled,
    setSubCategoryItemsEnabled,
  ] = useState<boolean>(false);

  const [subcategorySelections, setSubcategorySelections] = useState<
    Map<string, string[]>
  >(new Map());
  const [subcategoryItemSelections, setSubcategoryItemSelections] = useState<
    Map<string, string>
  >(new Map());

  useEffect(() => {
    if (product) {
      if (product.categories) {
        setSelectedCategories(product.categories);
      }

      if (product.subCategories && product.subCategories.length > 0) {
        setSubcategoriesEnabled(true);
        const subCatMap = new Map<string, string[]>();

        product.subCategories.forEach(subCategory => {
          const category = product.categories.find(c =>
            c.subCategories?.some(sc => sc.id === subCategory.id)
          );
          if (category) {
            const existing = subCatMap.get(category.id) || [];
            subCatMap.set(category.id, [...existing, subCategory.id]);
          }
        });

        setSubcategorySelections(subCatMap);
      }

      if (product.subCategoriesItems && product.subCategoriesItems.length > 0) {
        setSubCategoryItemsEnabled(true);
        const subCatItemMap = new Map<string, string>();

        product.subCategoriesItems.forEach(item => {
          const subCategory = product.subCategories?.find(sc =>
            sc.subCategoriesItems?.some(sci => sci.id === item.id)
          );
          if (subCategory) {
            subCatItemMap.set(subCategory.id, item.id);
          }
        });

        setSubcategoryItemSelections(subCatItemMap);
      }
    }
  }, [product]);

  const categoriesWithSubcategories = useMemo(() => {
    return selectedCategories.filter(
      c => c.subCategories && c.subCategories.length > 0
    );
  }, [selectedCategories]);

  const selectedSubcategoriesWithItems = useMemo(() => {
    const result: Subcategory[] = [];

    categoriesWithSubcategories.forEach(category => {
      const subcategoryIds = subcategorySelections.get(category.id) || [];
      subcategoryIds.forEach(subcategoryId => {
        if (category.subCategories) {
          const subcategory = category.subCategories.find(
            sc => sc.id === subcategoryId
          );
          if (subcategory) {
            result.push(subcategory);
          }
        }
      });
    });

    return result.filter(
      sc => sc.subCategoriesItems && sc.subCategoriesItems.length > 0
    );
  }, [categoriesWithSubcategories, subcategorySelections]);

  const handleCategoryChange = useCallback(
    (event: any, newCategories: Category[]) => {
      setSelectedCategories(newCategories);

      const newCategoryIds = new Set(newCategories.map(c => c.id));
      const newSubcategorySelections = new Map(subcategorySelections);

      newSubcategorySelections.forEach((subcategoryIds, categoryId) => {
        if (!newCategoryIds.has(categoryId)) {
          newSubcategorySelections.delete(categoryId);

          const newSubcategoryItemSelections = new Map(
            subcategoryItemSelections
          );
          subcategoryIds.forEach(subcategoryId => {
            newSubcategoryItemSelections.delete(subcategoryId);
          });
          setSubcategoryItemSelections(newSubcategoryItemSelections);
        }
      });

      setSubcategorySelections(newSubcategorySelections);
    },
    [subcategorySelections, subcategoryItemSelections]
  );

  const handleSubcategoryChange = useCallback(
    (categoryId: string, selectedSubcategoryIds: string[]) => {
      const newSelections = new Map(subcategorySelections);
      const oldSubcategoryIds = newSelections.get(categoryId) || [];

      // Remove item selections for deselected subcategories
      const newItemSelections = new Map(subcategoryItemSelections);
      oldSubcategoryIds.forEach(oldId => {
        if (!selectedSubcategoryIds.includes(oldId)) {
          newItemSelections.delete(oldId);
        }
      });
      setSubcategoryItemSelections(newItemSelections);

      if (selectedSubcategoryIds.length === 0) {
        newSelections.delete(categoryId);
      } else {
        newSelections.set(categoryId, selectedSubcategoryIds);
      }

      setSubcategorySelections(newSelections);
    },
    [subcategorySelections, subcategoryItemSelections]
  );

  const handleSubcategoryItemChange = useCallback(
    (subcategoryId: string, itemId: string) => {
      const newSelections = new Map(subcategoryItemSelections);

      if (itemId === 'none') {
        newSelections.delete(subcategoryId);
      } else {
        newSelections.set(subcategoryId, itemId);
      }

      setSubcategoryItemSelections(newSelections);
    },
    [subcategoryItemSelections]
  );

  const handleToggleSubcategories = useCallback((event: any) => {
    const enabled = event.target.checked;
    setSubcategoriesEnabled(enabled);

    if (!enabled) {
      setSubcategorySelections(new Map());
      setSubcategoryItemSelections(new Map());
      setSubCategoryItemsEnabled(false);
    }
  }, []);

  const handleToggleSubcategoryItems = useCallback((event: any) => {
    const enabled = event.target.checked;
    setSubCategoryItemsEnabled(enabled);

    if (!enabled) {
      setSubcategoryItemSelections(new Map());
    }
  }, []);

  useEffect(() => {
    const subCategories: Subcategory[] = [];
    categoriesWithSubcategories.forEach(category => {
      const subcategoryIds = subcategorySelections.get(category.id) || [];
      subcategoryIds.forEach(subcategoryId => {
        if (category.subCategories) {
          const subcategory = category.subCategories.find(
            sc => sc.id === subcategoryId
          );
          if (subcategory) {
            subCategories.push(subcategory);
          }
        }
      });
    });

    const subCategoryItems: SubcategoryItem[] = [];
    subCategories.forEach(subcategory => {
      const itemId = subcategoryItemSelections.get(subcategory.id);
      if (itemId && subcategory.subCategoriesItems) {
        const item = subcategory.subCategoriesItems.find(
          item => item.id === itemId
        );
        if (item) {
          subCategoryItems.push(item);
        }
      }
    });

    onChange(
      selectedCategories,
      subcategoriesEnabled ? subCategories : [],
      subCategoryItemsEnabled ? subCategoryItems : []
    );
  }, [
    selectedCategories,
    subcategorySelections,
    subcategoryItemSelections,
    categoriesWithSubcategories,
    subcategoriesEnabled,
    subCategoryItemsEnabled,
    onChange,
  ]);

  const handleOptionSelected = (option: Category, value: Category) =>
    option.id === value.id;

  return (
    <Box className={classes.container}>
      {/* Category Selection */}
      <div className={classes.categoryHeader}>
        <Typography className={classes.sectionTitle}>Categoría</Typography>
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
          <Tooltip
            placement="left"
            enterTouchDelay={0}
            classes={{tooltip: classes.categoryTooltip}}
            title={
              <div>
                Crea una nueva categoría y luego <br /> actualiza la página para
                visualizarla
              </div>
            }
          >
            <InfoOutlined color="primary" className={classes.infoBtn} />
          </Tooltip>
          {store && (
            <CustomButton
              variant="outlined"
              href={`https://${getBasicDomain(store.domains)}/admin/categories`}
              target="_blank"
              rel="noopener noreferrer"
            >
              + Crear categoría
            </CustomButton>
          )}
        </div>
      </div>

      <Typography className={classes.sectionDescription}>
        Selecciona la categoría a la que pertenece tu producto. Un producto
        puede tener una o más categorías.
      </Typography>

      <Autocomplete
        multiple
        id="product-categories"
        options={categories}
        value={selectedCategories}
        getOptionLabel={category => category.name}
        filterSelectedOptions
        onChange={handleCategoryChange}
        getOptionSelected={handleOptionSelected}
        getOptionDisabled={category => !category.enabled}
        disableClearable
        disabled={disabled}
        renderInput={params => (
          <CustomTextField
            {...params}
            multiline
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
        ChipProps={{
          variant: 'outlined',
          className: classes.categoryChip,
        }}
      />

      {/* Subcategories Section */}
      {categoriesWithSubcategories.length > 0 && (
        <>
          <Divider className={classes.divider} />

          <Typography className={classes.sectionTitle}>
            Subcategorías de producto
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={subcategoriesEnabled}
                onChange={handleToggleSubcategories}
                color="primary"
                disabled={disabled}
              />
            }
            className={classes.sectionCheckbox}
            label="Asignar subcategorías al producto"
          />

          {subcategoriesEnabled && (
            <>
              <Typography className={classes.helperText}>
                <InfoOutlined fontSize="small" />
                Puedes seleccionar una o más subcategorías por categoría
              </Typography>

              <Grid
                container
                spacing={2}
                className={classes.subcategorySection}
              >
                {categoriesWithSubcategories.map((category: Category) => (
                  <Grid
                    item
                    xs={12}
                    md={8}
                    key={`subcategory-select-${category.id}`}
                  >
                    <Typography className={classes.subcategoryTitle}>
                      {category.name}
                    </Typography>
                    <Autocomplete
                      multiple
                      id={`subcategory-autocomplete-${category.id}`}
                      options={category.subCategories || []}
                      value={
                        category.subCategories?.filter(sc =>
                          (
                            subcategorySelections.get(category.id) || []
                          ).includes(sc.id)
                        ) || []
                      }
                      getOptionLabel={subcategory => subcategory.name}
                      filterSelectedOptions
                      disabled={disabled}
                      onChange={(event, selectedSubcategories) => {
                        handleSubcategoryChange(
                          category.id,
                          selectedSubcategories.map(sc => sc.id)
                        );
                      }}
                      renderInput={params => (
                        <CustomTextField
                          {...params}
                          label={`Elige subcategorías dentro de ${category.name}`}
                          multiline
                          InputProps={{
                            ...params.InputProps,
                          }}
                        />
                      )}
                      ChipProps={{
                        variant: 'outlined',
                        className: classes.categoryChip,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {/* Subcategory Items Section */}
      {subcategoriesEnabled && selectedSubcategoriesWithItems.length > 0 && (
        <>
          <Divider className={classes.divider} />

          <Typography className={classes.sectionTitle}>
            Agregar subitems a tu producto
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={subCategoryItemsEnabled}
                onChange={handleToggleSubcategoryItems}
                color="primary"
                disabled={disabled}
              />
            }
            className={classes.sectionCheckbox}
            label="Asignar sub items al producto"
          />

          {subCategoryItemsEnabled && (
            <>
              <Typography className={classes.helperText}>
                <InfoOutlined fontSize="small" />
                Solo puedes seleccionar un item por subcategoría
              </Typography>

              <Grid
                container
                spacing={2}
                className={classes.subcategorySection}
              >
                {selectedSubcategoriesWithItems.map(
                  (subcategory: Subcategory) => (
                    <Grid
                      item
                      xs={12}
                      md={8}
                      key={`subcategory-item-select-${subcategory.id}`}
                    >
                      <Typography className={classes.subcategoryTitle}>
                        {subcategory.name}
                      </Typography>
                      <CustomSelect
                        native
                        fullWidth
                        label={`Elige el item dentro de ${subcategory.name}`}
                        className={`${classes.selectWrapper} ${classes.select}`}
                        value={
                          subcategoryItemSelections.get(subcategory.id) ||
                          'none'
                        }
                        disabled={disabled}
                        onChange={e => {
                          handleSubcategoryItemChange(
                            subcategory.id,
                            e.target.value
                          );
                        }}
                      >
                        <option value="none">Sin item</option>
                        {subcategory.subCategoriesItems?.map(item => (
                          <option
                            key={`subcategory-item-${item.id}`}
                            value={item.id}
                          >
                            {item.name}
                          </option>
                        ))}
                      </CustomSelect>
                    </Grid>
                  )
                )}
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );
}
